/**
 * CRM AuthContext — delegates authentication to @platform/auth-sdk
 * and layers CRM-specific user/billing/organization data on top.
 *
 * The Platform SDK (AuthProvider from @platform/auth-sdk) is the single
 * source of truth for tokens and authentication state. This context
 * consumes the SDK hooks and enriches them with CRM backend profile data.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth as usePlatformAuth, useUser as usePlatformUser } from '@platform/auth-sdk';
import { getCurrentUser, logoutApi, agentLogout } from '../../services/endpoint';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // SDK auth state (tokens, isAuthenticated, login/logout)
  const {
    isAuthenticated,
    isLoading: sdkLoading,
    login: sdkLogin,
    logout: sdkLogout,
    getAccessToken,
  } = usePlatformAuth();

  const { user: sdkUser } = usePlatformUser();

  // CRM-specific profile data fetched from backend
  const [user, setUser] = useState(null);
  const [billing, setBilling] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [lastFetch, setLastFetch] = useState(0);
  const hasInitializedRef = React.useRef(false);

  // Derived: whether user has a valid token (delegates to SDK)
  const hasToken = isAuthenticated;

  // Keep sessionStorage in sync for backward compatibility.
  // Many WebSocket and component files still read sessionStorage.getItem('access_token')
  // directly for building WS URLs. This bridge ensures they get the SDK-managed token.
  useEffect(() => {
    const syncToken = async () => {
      if (isAuthenticated) {
        const token = await getAccessToken();
        if (token) {
          sessionStorage.setItem('access_token', token);
        }
      } else {
        sessionStorage.removeItem('access_token');
      }
    };
    syncToken();
  }, [isAuthenticated, getAccessToken]);

  const fetchUser = useCallback(async (force = false) => {
    try {
      if (!isAuthenticated) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      // Prevent excessive API calls — only fetch once per 30 seconds unless forced
      const now = Date.now();
      if (!force && (now - lastFetch) < 30000 && user) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      const response = await getCurrentUser();
      const res = response?.data?.data;
      sessionStorage.setItem('user_role', res?.user?.role);
      setUser(res?.user);
      setBilling({ ...res?.billing, services: res?.services });
      setOrganization(res?.organization);
      setLastFetch(now);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem('user_role');
        setUser(null);
      }
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, [isAuthenticated, lastFetch, user]);

  const logout = useCallback(async () => {
    try {
      // Call CRM backend logout APIs
      const userRole = sessionStorage.getItem('user_role');
      if (userRole === 'agent' || userRole === 'supervisor') {
        await agentLogout();
      } else {
        await logoutApi();
      }
    } catch (error) {
      // Continue with frontend logout even if API call fails
    }

    // Set logout flag in localStorage for cross-tab sync
    localStorage.setItem('logout', Date.now().toString());

    // Clear CRM-specific session data
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('sidebarMenus');
    setUser(null);
    setBilling(null);
    setOrganization(null);

    // Trigger auth state change event to stop timers
    window.dispatchEvent(new Event('authStateChange'));

    // Delegate to SDK logout (revokes refresh token + redirects to Identity Center)
    await sdkLogout();
  }, [sdkLogout]);

  // Initialize CRM profile when SDK reports authenticated
  useEffect(() => {
    if (sdkLoading) return;

    if (isAuthenticated && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchUser(true);
    } else if (!isAuthenticated) {
      hasInitializedRef.current = false;
      setUser(null);
      setBilling(null);
      setOrganization(null);
      setLoading(false);
      setAuthChecked(true);
    }
  }, [isAuthenticated, sdkLoading, fetchUser]);

  // Cross-tab logout synchronization
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout' && e.newValue) {
        sessionStorage.removeItem('user_role');
        sessionStorage.removeItem('sidebarMenus');
        setUser(null);
        setBilling(null);
        setOrganization(null);
        // SDK handles the actual auth state invalidation
        sdkLogout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [sdkLogout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        logout,
        login: sdkLogin,
        fetchUser,
        billing,
        organization,
        loading: loading || sdkLoading,
        authChecked: authChecked && !sdkLoading,
        hasToken,
        currentPath,
        setCurrentPath,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
